import { CategoryType, COLLECTION_SLUGS, CourseStatus, CourseType, LessonStatus, LIMITS } from '@/lib/constants'
import { courseHiddenFromDraftViewers, effectiveMinStatusForCourse } from '@/lib/draftVisibility'
import { ResourceNotFoundError, ValidationError } from '@/lib/errors'
import { handleSupabaseList, handleSupabaseSingle, supabase } from '@/lib/supabase-unified'
import type { Course, CourseThumbnailItem, Lesson, LessonVideo, Section, UserLesson } from '@/types'
import { lessonThumbnailProgressPercent } from '@/utils/lessonProgressPercent'
import { getCourseBySlug } from './courses'

interface CoursePageData {
  course: Course
  sections: Section[]
  userLessonsMap: Record<number, UserLesson>
}

// Internal type definitions for better type safety
interface LessonCategory {
  lesson: Lesson
  position: number
}

interface CategoryWithLessons {
  id: number
  title: string
  type: number
  slug: string
  course_id: number
  lesson_categories: LessonCategory[]
}

interface Collection {
  id: number
  slug: string
  title: string
  type: number
  thumbnail: string | null
  color: string | null
}

interface CategoryBasic {
  id: number
  title: string
  type: number
  slug: string
}

export interface SkillPathway {
  id: number
  title: string
  slug: string
  section_id: number
  section_title: string
  section_slug: string
  position: number
  thumbnail?: string | null
  color?: string | null
}

export async function fetchUserCourseProgress(
  userId: string | null,
  courseIds: number[]
): Promise<Record<number, number>> {
  if (!userId || courseIds.length === 0) return {}

  const rows = await handleSupabaseList<{ course_id: number; progress: number | null }>(
    supabase
      .from('user_course_interactions')
      .select('course_id, progress')
      .eq('user_id', userId)
      .in('course_id', courseIds) as any
  )

  return Object.fromEntries(
    rows.map((r) => [r.course_id, Math.min(1, Math.max(0, r.progress ?? 0))])
  ) as Record<number, number>
}

export interface CourseThumbnailsCategory {
  id: number
  title: string
  slug: string
  courses: CourseThumbnailItem[]
}

interface UserLessonWithCourse {
  id: string
  created_at: string
  last_updated_at: string
  user_id: string
  lesson_id: number
  is_opened: boolean
  is_completed: boolean
  language?: string
  left_off_at_seconds?: number
  progress?: number | null
  completed_video_count?: number | null
  lessons: {
    course_id: number
  }
}

function withMainWorkTitle<T extends { work?: { title?: string | null } | null }>(
  lesson: T,
): Omit<T, 'work'> & { main_work_title: string | null } {
  const { work, ...rest } = lesson
  return {
    ...rest,
    main_work_title: work?.title ?? null,
  }
}

/**
 * "Remove from Continue watching" for a single lesson. The RPC stamps
 * user_lesson_interactions.continue_hidden_at server-side (same transaction
 * clock as the set_last_updated_at trigger, so the row compares as hidden);
 * watching the lesson again bumps last_updated_at and resurfaces it.
 */
export async function hideLessonFromContinueWatching(
  lessonId: number
): Promise<void> {
  const { error } = await supabase.rpc('hide_lesson_from_continue', {
    p_lesson_id: lessonId,
  })
  if (error) throw error
}

export async function fetchContinueLessonsForDashboard(
  userId: string | null,
  dashboardCourseId: number | undefined,
  simpleCourseIds: number[],
  minStatus: number,
  linkedCourseIds: number[] = [],
  showAll: boolean = false,
  includeAllCourses: boolean = false
): Promise<Lesson[]> {
  if (!userId) return []

  const allCourseIds = [
    ...(dashboardCourseId != null ? [dashboardCourseId] : []),
    ...simpleCourseIds,
    ...linkedCourseIds,
  ]

  if (!includeAllCourses && allCourseIds.length === 0) return []

  const baseQuery = supabase
    .from('user_lesson_interactions')
    .select(
      `*,
      lessons!inner (
        id, title, short_title, week_number, main_work, slug, thumbnail, duration, video_count, course_id,
        published_at, summary, status,
        work:works!lessons_main_work_fkey (
          title
        ),
        courses (title, slug, status, metadata)
      )`
    )
    .eq('user_id', userId)
    .eq('is_completed', false)
    .gt('left_off_at_seconds', 0)

  const effectiveRows = includeAllCourses
    ? await handleSupabaseList<any>(baseQuery as any)
    : await handleSupabaseList<any>(baseQuery.in('lessons.course_id', allCourseIds) as any)

  const byCourse = new Map<number, any[]>()
  for (const row of effectiveRows) {
    const cid = row.lessons?.course_id
    if (cid == null) continue
    // Dismissed via "Remove from Continue watching": hidden until the lesson
    // is watched again (last_updated_at moves past continue_hidden_at).
    if (
      row.continue_hidden_at &&
      new Date(row.last_updated_at ?? 0).getTime() <=
        new Date(row.continue_hidden_at).getTime()
    )
      continue
    // hide_from_draft_viewers: non-published content of a flagged course must
    // not resurface here, not even for allowlisted draft viewers.
    const course = row.lessons?.courses
    if (
      courseHiddenFromDraftViewers(course?.metadata) &&
      ((row.lessons?.status ?? 0) < CourseStatus.Published ||
        (course?.status ?? 0) < CourseStatus.Published)
    )
      continue
    if (!byCourse.has(cid)) byCourse.set(cid, [])
    byCourse.get(cid)!.push(row)
  }

  const sorted = (r: any[]) =>
    r.sort((a, b) => new Date(b.last_updated_at ?? '').getTime() - new Date(a.last_updated_at ?? '').getTime())

  const toLesson = (row: any) => {
    const l = withMainWorkTitle(row.lessons)
    const c = l.courses
    const isFromOtherCourse =
      dashboardCourseId == null || l.course_id !== dashboardCourseId
    const dur = l.duration ?? 0
    const progressPercentage = lessonThumbnailProgressPercent({
      userProgress: row.progress,
      videoCount: l.video_count,
      completedVideoCount: row.completed_video_count,
      leftOffAtSeconds: row.left_off_at_seconds,
      durationSeconds: dur,
    })
    return {
      id: l.id,
      title: isFromOtherCourse ? (c?.title ?? l.title) : l.title,
      short_title: isFromOtherCourse
        ? (l.short_title?.trim() || l.title)
        : l.short_title,
      week_number: l.week_number,
      main_work: l.main_work,
      main_work_title: l.main_work_title,
      slug: l.slug,
      thumbnail: l.thumbnail,
      duration: l.duration,
      video_count: l.video_count,
      course_id: l.course_id,
      published_at: l.published_at,
      summary: l.summary,
      status: l.status,
      progressPercentage,
      course_title: isFromOtherCourse ? c?.title : undefined,
      course_slug: isFromOtherCourse ? c?.slug : undefined,
    } as Lesson
  }

  if (showAll) {
    return [...byCourse.values()]
      .flatMap((courseRows) => sorted(courseRows))
      .sort((a, b) => new Date(b.last_updated_at ?? '').getTime() - new Date(a.last_updated_at ?? '').getTime())
      .slice(0, LIMITS.COLLECTION_SEE_ALL)
      .map(toLesson)
  }

  // Dashboard section: up to 5 from dashboard course (when set) + 1 per type3/type4 course
  const dashboardRows =
    dashboardCourseId != null
      ? sorted(byCourse.get(dashboardCourseId) || []).slice(0, 5)
      : []
  const otherRows = includeAllCourses
    ? [...byCourse.keys()]
        .filter((cid) => dashboardCourseId == null || cid !== dashboardCourseId)
        .flatMap((cid) => sorted(byCourse.get(cid) || []).slice(0, 1))
    : [...simpleCourseIds, ...linkedCourseIds].flatMap((cid) =>
        sorted(byCourse.get(cid) || []).slice(0, 1)
      )
  return [...dashboardRows, ...otherRows]
    .sort((a, b) => new Date(b.last_updated_at ?? '').getTime() - new Date(a.last_updated_at ?? '').getTime())
    .map(toLesson)
}

export async function fetchSectionsWithLessons(courseId: number, minStatus: number): Promise<CategoryWithLessons[]> {
  const rows = await handleSupabaseList<any>(
    supabase
      .from('category')
      .select(`
        id,
        title,
        type,
        slug,
        course_id,
        lesson_categories (
          position,
          lesson:lessons (
            id,
            title,
            short_title,
            week_number,
            main_work,
            thumbnail,
            duration,
            video_count,
            course_id,
            published_at,
            summary,
            slug,
            status,
            work:works!lessons_main_work_fkey (
              title
            ),
            courses (slug, title)
          )
        )
      `)
      .eq('course_id', courseId)
      .in('type', [CategoryType.Section, CategoryType.LinkedCourseSection])
      .neq('slug', COLLECTION_SLUGS.LATEST)
      .gte('lesson_categories.lesson.status', minStatus)
      .order('position', { ascending: true })
      .order('position', { referencedTable: 'lesson_categories', ascending: true })
      .order('published_at', {
        referencedTable: 'lesson_categories.lesson',
        ascending: false
      }) as any
  )

  return rows.map((cat: any) => {
    const lesson_categories = (cat.lesson_categories ?? []).map((lc: any) => {
      const lesson = lc.lesson
      if (!lesson) return lc
      const isFromOtherCourse = lesson.course_id !== courseId
      const { courses, ...lessonData } = withMainWorkTitle(lesson)
      if (isFromOtherCourse) {
        lessonData.course_slug = courses?.slug
        lessonData.course_title = courses?.title
      }
      return { ...lc, lesson: lessonData }
    })
    return { ...cat, lesson_categories } as CategoryWithLessons
  })
}

export interface LinkedCourseData {
  courseIds: number[]
  slugsByCategoryId: Record<number, string>
}

export async function fetchLinkedCourses(
  courseId: number,
  minStatus: number = CourseStatus.Published
): Promise<LinkedCourseData> {
  const rows = await handleSupabaseList<any>(
    supabase
      .from('category')
      .select('id, position, title, slug, course_categories(course:courses(id, slug, status, metadata))')
      .eq('course_id', courseId)
      .gte('course_categories.course.status', minStatus)
      .eq('type', CategoryType.LinkedCourseSection)
      .order('position', { ascending: true, nullsFirst: false }) as any
  )

  const sortedLinkedRows = [...rows].sort(
    (a: any, b: any) => (a.position ?? 999) - (b.position ?? 999)
  )

  const courseIds: number[] = []
  const slugsByCategoryId: Record<number, string> = {}
  for (const cat of sortedLinkedRows) {
    const course = cat.course_categories?.[0]?.course
    if (!course || (course.status ?? 0) < effectiveMinStatusForCourse(minStatus, course.metadata)) continue
    courseIds.push(course.id)
    slugsByCategoryId[cat.id] = course.slug
  }
  return { courseIds, slugsByCategoryId }
}

/**
 * Fetch type 2 categories (collections)
 */
export async function fetchCollections(courseId: number): Promise<Collection[]> {
  return handleSupabaseList<Collection>(
    supabase
      .from('category')
      .select('id, slug, title, type, thumbnail, color')
      .eq('course_id', courseId)
      .eq('type', CategoryType.Collection)
      .order('position', { ascending: true }) as any
  )
}

export async function fetchCourseThumbnailsCategories(
  courseId: number,
  minStatus: number = CourseStatus.Published
): Promise<CourseThumbnailsCategory[]> {
  let rows: any[]
  try {
    rows = await handleSupabaseList<any>(
      supabase
        .from('category')
        .select(
          `
            id,
            title,
            slug,
            type,
            position,
            course_categories (
              position,
              course:courses (
                id,
                title,
                slug,
                thumbnail,
                type,
                status,
                metadata
              )
            )
          `
        )
        .eq('course_id', courseId)
        .eq('type', CategoryType.CourseCategory)
        .gte('course_categories.course.status', minStatus)
        .order('position', { ascending: true, nullsFirst: false })
        .order('position', { referencedTable: 'course_categories', ascending: true }) as any
    )
  } catch (err) {
    console.warn('[Course thumbnails] Query failed:', err)
    return []
  }

  const sortedParentRows = [...rows].sort(
    (a: any, b: any) => (a.position ?? 999) - (b.position ?? 999)
  )

  const sections = sortedParentRows
    .map((cat: any) => {
      if (!cat) return null

      const sortedCc = [...(cat.course_categories ?? [])].sort(
        (a: any, b: any) => (a.position ?? 999) - (b.position ?? 999)
      )

      const courses: CourseThumbnailItem[] = sortedCc
        .filter(
          (cc: any) =>
            cc.course &&
            cc.course.id !== courseId &&
            cc.course.type === CourseType.Simple &&
            (cc.course.status ?? 0) >= effectiveMinStatusForCourse(minStatus, cc.course.metadata)
        )
        .map((cc: any) => ({
          id: cc.course.id,
          title: cc.course.title,
          slug: cc.course.slug,
          thumbnail: cc.course.thumbnail ?? null,
        }))

      if (courses.length === 0) return null

      return {
        id: cat.id,
        title: cat.title,
        slug: cat.slug,
        courses,
      } as CourseThumbnailsCategory
    })
    .filter((s): s is CourseThumbnailsCategory => s !== null)

  return sections
}

/**
 * Fetch the "latest" category if it exists (optional)
 * TODO remove this, we don't need the DB for this.
 * We should just use the lessons from the sections.
 */
export async function fetchLatestCategory(courseId: number): Promise<CategoryBasic | null> {
  try {
    return await handleSupabaseSingle<CategoryBasic>(
      supabase
        .from('category')
        .select('id, title, type, slug')
        .eq('course_id', courseId)
        .eq('slug', COLLECTION_SLUGS.LATEST)
        .single() as any
    )
  } catch (error) {
    // Latest category is optional, return null if not found
    if (error instanceof ResourceNotFoundError) {
      return null
    }
    throw error
  }
}

/**
 * Fetch the most recently published lessons
 */
export async function fetchLatestLessons(courseId: number, limit: number, minStatus: number): Promise<Lesson[]> {
  const rows = await handleSupabaseList<any>(
    supabase
      .from('lessons')
      .select(`id,
        title,
        short_title,
        week_number,
        main_work,
        thumbnail,
        duration,
        video_count,
        course_id,
        published_at,
        summary,
        slug,
        work:works!lessons_main_work_fkey (
          title
        )`)
      .eq('course_id', courseId)
      .not('published_at', 'is', null)
      .gte('status', minStatus)
      .order('published_at', { ascending: false, nullsFirst: false })
      .limit(limit) as any
  )
  return rows.map((row) => withMainWorkTitle(row) as Lesson)
}

export interface SkillPathwaySectionData {
  id: number
  title: string
  slug: string
  position: number
  pathways: SkillPathway[]
}

export async function fetchSkillPathwaysForDashboard(courseId: number): Promise<SkillPathwaySectionData[]> {
  const sections = await handleSupabaseList<{ id: number; title: string; slug: string; position: number | null }>(
    supabase
      .from('category')
      .select('id, title, slug, position')
      .eq('course_id', courseId)
      .eq('type', CategoryType.SkillPathwaySection) as any
  )

  if (!sections.length) return []

  const sectionIds = sections.map((s) => s.id)

  const links = await handleSupabaseList<{
    skill_pathway_section_id: number
    position: number | null
    pathway: { id: number; title: string; slug: string; thumbnail: string | null; color: string | null } | null
  }>(
    supabase
      .from('category_categories')
      .select('skill_pathway_section_id, position, pathway:skill_pathway_id ( id, title, slug, thumbnail, color )')
      .in('skill_pathway_section_id', sectionIds)
      .order('position', { ascending: true }) as any
  )

  const result: SkillPathwaySectionData[] = sections.map((s) => ({
    id: s.id,
    title: s.title,
    slug: s.slug,
    position: s.position ?? 999,
    pathways: [],
  }))

  const sectionMap = new Map(result.map((s) => [s.id, s]))

  for (const link of links) {
    const section = sectionMap.get(link.skill_pathway_section_id)
    if (!section || !link.pathway) continue

    section.pathways.push({
      id: link.pathway.id,
      title: link.pathway.title,
      slug: link.pathway.slug,
      section_id: section.id,
      section_title: section.title,
      section_slug: section.slug,
      position: link.position ?? 0,
      thumbnail: link.pathway.thumbnail ?? null,
      color: link.pathway.color ?? null,
    })
  }

  return result.filter((s) => s.pathways.length > 0)
}

/**
 * Fetch all lessons for a course in display order (for simple course – no sections).
 * No "Continue watch" or "Latest" – just lessons ordered by order_no.
 */
export async function fetchLessonsForCourse(courseId: number, minStatus: number): Promise<Lesson[]> {
  const rows = await handleSupabaseList<any>(
    supabase
      .from('lessons')
      .select(`
        id,
        title,
        short_title,
        week_number,
        main_work,
        thumbnail,
        duration,
        video_count,
        course_id,
        published_at,
        summary,
        slug,
        status,
        order_no,
        work:works!lessons_main_work_fkey (
          title
        )
      `)
      .eq('course_id', courseId)
      .gte('status', minStatus)
      .order('order_no', { ascending: true, nullsFirst: false }) as any
  )
  return rows.map((row) => withMainWorkTitle(row) as Lesson)
}

/**
 * Lesson videos + chapters for sidebar preview (no full lesson page RPC).
 */
export async function fetchLessonVideosWithChapters(lessonId: number): Promise<LessonVideo[]> {
  const rows = await handleSupabaseList<any>(
    supabase
      .from('lesson_videos')
      .select(
        `
        id,
        lesson_id,
        title,
        slug,
        ss_url,
        duration,
        order_no,
        summary,
        description,
        thumbnail,
        lesson_video_chapters (
          id,
          lesson_video_id,
          order_no,
          begin,
          title,
          description
        )
      `,
      )
      .eq('lesson_id', lessonId)
      .order('order_no', { ascending: true, nullsFirst: false }) as any,
  )

  return rows.map((row: any) => {
    const { lesson_video_chapters, ...rest } = row
    const raw = Array.isArray(lesson_video_chapters) ? lesson_video_chapters : []
    const chapters = [...raw]
      .sort((a: any, b: any) => (a.order_no ?? 0) - (b.order_no ?? 0))
      .map((ch: any) => ({
        ...ch,
        summary: typeof ch.description === 'string' ? ch.description : '',
        timestamp: typeof ch.begin === 'number' ? ch.begin : 0,
      }))
    return { ...rest, chapters } as LessonVideo
  })
}

/**
 * Fetch user lessons for one or more courses.
 */
export async function fetchUserLessonsForCourse(
  userId: string | null,
  courseIds: number | number[],
  includeContinueLessons = true
): Promise<{
  allUserLessons: UserLessonWithCourse[]
  continueLessons: Lesson[]
}> {
  if (!userId) {
    return {
      allUserLessons: [],
      continueLessons: []
    }
  }

  const ids = Array.isArray(courseIds) ? courseIds : [courseIds]
  if (ids.length === 0) {
    return {
      allUserLessons: [],
      continueLessons: []
    }
  }

  const allUserLessons = await handleSupabaseList<UserLessonWithCourse>(
    supabase
      .from('user_lesson_interactions')
      .select(`
        *,
        lessons!inner (
          course_id
        )
      `)
      .eq('user_id', userId)
      .in('lessons.course_id', ids) as any
  )

  if (!includeContinueLessons) {
    return {
      allUserLessons,
      continueLessons: [],
    }
  }

  // Get top 5 incomplete lessons, sorted by most recently updated
  const continueUserLessons = allUserLessons
    .filter((ul) => !ul.is_completed && ul.left_off_at_seconds && ul.left_off_at_seconds > 0)
    .sort((a, b) => new Date(b.last_updated_at ?? '').getTime() - new Date(a.last_updated_at ?? '').getTime())
    .slice(0, 5)

  // If no lessons to continue, return early
  if (continueUserLessons.length === 0) {
    return {
      allUserLessons,
      continueLessons: []
    }
  }

  // Fetch the lesson details for the continue lessons
  const lessonIds = continueUserLessons.map((ul) => ul.lesson_id)
  const fetchedLessons = await handleSupabaseList<Lesson>(
    supabase
      .from('lessons')
      .select(`
        id,
        title,
        short_title,
        week_number,
        main_work,
        slug,
        thumbnail,
        duration,
        video_count,
        course_id,
        published_at,
        summary,
        work:works!lessons_main_work_fkey (
          title
        )
      `)
      .in('id', lessonIds) as any
  )

  // Sort lessons to match the order of continueUserLessons
  const lessonMap = new Map(
    fetchedLessons.map((lesson: any) => [lesson.id, withMainWorkTitle(lesson) as Lesson]),
  )
  const continueLessons = lessonIds
    .map(id => lessonMap.get(id))
    .filter((lesson): lesson is Lesson => lesson !== undefined)

  return {
    allUserLessons,
    continueLessons
  }
}

/**
 * Build user lessons map from raw data
 */
export function buildUserLessonsMap(userLessonsData: UserLessonWithCourse[]): Record<number, UserLesson> {
  const userLessonsMap: Record<number, UserLesson> = {}
  userLessonsData.forEach((ul) => {
    const { lessons: _lessons, last_updated_at, ...rest } = ul
    userLessonsMap[rest.lesson_id] = {
      ...rest,
      last_updated_at: last_updated_at ?? undefined,
    }
  })
  return userLessonsMap
}

/**
 * Courses where skill pathway blocks should render at the bottom of the dashboard
 * (temporary manual ordering). TODO: drive all section order from `category.position` (or equivalent).
 */
const COURSE_SLUGS_SKILL_PATHWAYS_AT_PAGE_END = new Set<string>(['jazz-piano-method'])

function skillPathwaysRenderAtPageEnd(courseSlug?: string | null): boolean {
  if (courseSlug == null || typeof courseSlug !== 'string') return false
  return COURSE_SLUGS_SKILL_PATHWAYS_AT_PAGE_END.has(courseSlug.trim().toLowerCase())
}

/**
 * Build sections array from categories and lessons data
 */
export function buildSections(
  categoriesWithLessons: CategoryWithLessons[],
  collections: Collection[],
  latestCategory: CategoryBasic | null,
  latestLessons: Lesson[],
  continueLessons: Lesson[],
  userLessonsMap: Record<number, UserLesson>,
  courseThumbnailsCategories: CourseThumbnailsCategory[] = [],
  skillPathwaySections: { id: number; title: string; slug: string; position: number; pathways: SkillPathway[] }[] = [],
  courseSlug?: string | null
): Section[] {
  const sections: Section[] = []
  const pathwaysAtEnd = skillPathwaysRenderAtPageEnd(courseSlug)

  const appendSkillPathwaySections = () => {
    for (const sp of skillPathwaySections) {
      if (sp.pathways.length) {
        sections.push({
          id: sp.id,
          title: sp.title,
          slug: sp.slug,
          lessons: [],
          type: CategoryType.SkillPathwaySection,
          pathways: sp.pathways,
        })
      }
    }
  }

  // Helper function to build a section from a category
  const buildCategorySection = (category: CategoryWithLessons): Section => {
    const lessons: Lesson[] = []

    // Extract lessons from the lesson_categories join
    // Lessons are already ordered by position (asc) then published_at (desc) from the database
    if (category.lesson_categories && Array.isArray(category.lesson_categories)) {
      category.lesson_categories.forEach((lc) => {
        if (lc.lesson) {
          // Check if lesson already exists (avoid duplicates)
          if (!lessons.find(l => l.id === lc.lesson.id)) {
            lessons.push(lc.lesson)
          }
        }
      })
    }

    return {
      id: category.id,
      title: category.title,
      lessons: lessons,
      type: category.type,
      slug: category.slug,
    }
  }

  // Helper function to get the most recent last_updated_at from a section's lessons
  const getMostRecentActivity = (lessons: Lesson[]): Date => {
    let mostRecent = new Date(0) // Infinitely in the past
    
    lessons.forEach(lesson => {
      const userLesson = userLessonsMap[lesson.id]
      if (userLesson) {
        const updatedAt = new Date(userLesson.last_updated_at)
        if (updatedAt > mostRecent) {
          mostRecent = updatedAt
        }
      }
    })
    
    return mostRecent
  }

  // Build the latest section and first type 1 category section
  const latestSection = latestCategory ? {
    id: latestCategory.id,
    title: latestCategory.title,
    slug: latestCategory.slug || COLLECTION_SLUGS.LATEST,
    lessons: latestLessons,
    type: latestCategory.type
  } : null

  const firstCategorySection = (categoriesWithLessons.length > 0) 
    ? buildCategorySection(categoriesWithLessons[0]) 
    : null

  // Compare the two sections and add them in order of most recent activity
  if (latestSection && firstCategorySection) {
    const latestActivity = getMostRecentActivity(latestSection.lessons)
    const firstCategoryActivity = getMostRecentActivity(firstCategorySection.lessons)
    if (latestActivity > firstCategoryActivity) {
      sections.push(latestSection)
      sections.push(firstCategorySection)
    } else {
      sections.push(firstCategorySection)
      sections.push(latestSection)
    }
  } else if (latestSection) {
    sections.push(latestSection)
  } else if (firstCategorySection) {
    sections.push(firstCategorySection)
  }

  const continueSection = continueLessons.length > 0 ? {
    id: 0,
    title: 'Continue',
    slug: COLLECTION_SLUGS.CONTINUE,
    lessons: continueLessons,
    type: 1
  } : null

  if (continueSection) {
    sections.push(continueSection)
  }

  // 3. Skill Pathways — default: after Continue, before Collections.
  // Exception: see `COURSE_SLUGS_SKILL_PATHWAYS_AT_PAGE_END` (append after step 6).
  if (!pathwaysAtEnd) {
    appendSkillPathwaySections()
  }

  // 4. Collections
  if (collections.length > 0) {
    const collectionItems = collections.map(c => ({
      id: c.id,
      title: c.title,
      thumbnail: c.thumbnail,
      color: c.color,
      slug: c.slug
    } as unknown as Lesson))

    sections.push({
      title: 'Collections',
      slug: 'collections',
      lessons: collectionItems,
      type: CategoryType.Collection
    })
  }

  // 5. Linked simple courses — one carousel per category row (title/slug from `category` table, e.g. id 44)
  for (const cat of courseThumbnailsCategories) {
    const courseItems = cat.courses.map(
      (c) =>
        ({
          id: c.id,
          title: c.title,
          slug: c.slug,
          thumbnail: c.thumbnail,
        }) as unknown as Lesson
    )
    if (courseItems.length === 0) continue

    sections.push({
      id: cat.id,
      title: cat.title?.trim() || 'Courses',
      slug: cat.slug?.trim() || 'courses',
      lessons: courseItems,
      type: CategoryType.CourseCategory,
    })
  }

  // 6. Remaining sections (type 1/3) — ordered by position from DB
  for (let i = 1; i < categoriesWithLessons.length; i++) {
    sections.push(buildCategorySection(categoriesWithLessons[i]))
  }

  if (pathwaysAtEnd) {
    appendSkillPathwaySections()
  }

  return sections
}

/**
 * Fetch complete course page data (sections + lessons + user progress)
 */
export async function fetchCoursePageData(
  courseSlug: string,
  userId: string | null,
  latestLimit: number = LIMITS.DEFAULT_LATEST,
  minStatus: number = LessonStatus.Published
): Promise<CoursePageData> {
  // Validate input parameters
  if (!courseSlug || courseSlug.trim() === '') {
    throw new ValidationError('Course slug is required')
  }
  
  if (latestLimit <= 0 || !Number.isInteger(latestLimit)) {
    throw new ValidationError('latestLimit must be a positive integer')
  }

  // Fetch course by slug (throws ResourceNotFoundError if not found)
  const course = await getCourseBySlug(courseSlug)
  const courseId = course.id

  // Fetch all data in parallel
  const [
    categoriesWithLessons,
    collections,
    latestCategory,
    latestLessons,
    userLessonsResult,
    courseThumbnailsCategories
  ] = await Promise.all([
    fetchSectionsWithLessons(courseId, minStatus),
    fetchCollections(courseId),
    fetchLatestCategory(courseId),
    fetchLatestLessons(courseId, latestLimit, minStatus),
    fetchUserLessonsForCourse(userId, courseId),
    fetchCourseThumbnailsCategories(courseId, minStatus)
  ])

  const userLessonsMap = buildUserLessonsMap(userLessonsResult.allUserLessons)

  const sections = buildSections(
    categoriesWithLessons,
    collections,
    latestCategory,
    latestLessons,
    userLessonsResult.continueLessons,
    userLessonsMap,
    courseThumbnailsCategories
  )

  return {
    course,
    sections,
    userLessonsMap
  }
}
